/**
 * @fileoverview Email API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Email module.
 * Handles transactional emails (payment confirmation, test results delivery).
 */

import { z } from 'zod';
import { createContract, ApiSuccessResponseSchema } from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Email Template Type Enum
 */
export enum EmailTemplateType {
	PAYMENT_CONFIRMATION = 'payment_confirmation',
	TEST_RESULTS = 'test_results',
	REVIEW_REQUIRED = 'review_required',
	WORKFLOW_ERROR = 'workflow_error',
	CUSTOM = 'custom',
}

/**
 * Email Status Enum
 */
export enum EmailStatus {
	PENDING = 'pending',
	SENDING = 'sending',
	SENT = 'sent',
	FAILED = 'failed',
}

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Email Template Type Schema
 */
export const EmailTemplateTypeSchema = z.enum(EmailTemplateType);

/**
 * Email Status Schema
 */
export const EmailStatusSchema = z.enum(EmailStatus);

/**
 * Email Attachment Schema
 */
export const EmailAttachmentSchema = z.object({
	filename: z.string(),
	content: z.string(), // Base64 encoded
	contentType: z.string(),
});
export type EmailAttachment = z.infer<typeof EmailAttachmentSchema>;

/**
 * Send Email Request Schema
 */
export const SendEmailRequestSchema = z.object({
	to: z.email(),
	subject: z.string().min(1).max(200),
	html: z.string().min(1),
	attachments: z.array(EmailAttachmentSchema).optional(),
});
export type SendEmailRequest = z.infer<typeof SendEmailRequestSchema>;

/**
 * Send Email Response Schema
 */
export const SendEmailResponseSchema = z.object({
	success: z.boolean(),
	messageId: z.string().optional(),
	message: z.string(),
});
export type SendEmailResponse = z.infer<typeof SendEmailResponseSchema>;

/**
 * Send Template Email Request Schema
 */
export const SendTemplateEmailRequestSchema = z.object({
	to: z.email(),
	templateType: EmailTemplateTypeSchema,
	data: z.record(z.string(), z.unknown()),
	attachments: z.array(EmailAttachmentSchema).optional(),
});
export type SendTemplateEmailRequest = z.infer<typeof SendTemplateEmailRequestSchema>;

/**
 * Send Payment Confirmation Request Schema
 */
export const SendPaymentConfirmationRequestSchema = z.object({
	to: z.email(),
	paymentId: z.uuid(),
	customerName: z.string(),
	amount: z.number().positive(),
	currency: z.string().length(3),
});
export type SendPaymentConfirmationRequest = z.infer<typeof SendPaymentConfirmationRequestSchema>;

/**
 * Send Test Results Request Schema
 */
export const SendTestResultsRequestSchema = z.object({
	to: z.email(),
	testId: z.uuid(),
	customerName: z.string(),
	pdfAttachment: EmailAttachmentSchema.optional(),
});
export type SendTestResultsRequest = z.infer<typeof SendTestResultsRequestSchema>;

/**
 * Send Workflow Error Notification Request Schema
 */
export const SendWorkflowErrorNotificationRequestSchema = z.object({
	workflowId: z.uuid(),
	testId: z.uuid(),
	customerEmail: z.email(),
	errorMessage: z.string(),
	failedStep: z.string(),
	workflowType: z.string(),
});
export type SendWorkflowErrorNotificationRequest = z.infer<
	typeof SendWorkflowErrorNotificationRequestSchema
>;

/**
 * Email Source Schema
 *
 * Identifies where an email was triggered from.
 */
export const EmailSourceSchema = z.enum([
	'workflow', // Triggered by orchestration workflow
	'admin_manual', // Admin triggered manually
	'payment_webhook', // Payment confirmation
	'system', // System notifications
	'fulfillment', // PDF generation and delivery
]);
export type EmailSource = z.infer<typeof EmailSourceSchema>;

/**
 * Email Context Schema
 *
 * Flexible metadata for linking emails to workflows, tests, etc.
 * Stored as JSONB in database for query flexibility.
 *
 * Note: Uses .catchall(z.unknown()) to allow arbitrary metadata while
 * strictly validating known keys (workflowId, source, etc).
 */
export const EmailContextSchema = z
	.object({
		workflowId: z.string().uuid().optional(),
		testId: z.string().optional(),
		source: EmailSourceSchema.optional(),
	})
	.catchall(z.unknown());
export type EmailContext = z.infer<typeof EmailContextSchema>;

/**
 * Email Log Schema
 *
 * and ISO strings (from API). This allows @ZodResponse to work correctly with DB entities.
 */
export const EmailLogSchema = z.object({
	id: z.uuid(),
	to: z.email(),
	subject: z.string(),
	templateType: EmailTemplateTypeSchema.nullable().optional(),
	status: EmailStatusSchema,
	messageId: z.string().optional(),
	error: z.string().optional(),
	context: EmailContextSchema.nullish(),
	sentAt: z.iso.datetime().optional(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type EmailLog = z.infer<typeof EmailLogSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

export const SendEmailApiResponseSchema = ApiSuccessResponseSchema(SendEmailResponseSchema);
export const SendTemplateEmailApiResponseSchema = ApiSuccessResponseSchema(SendEmailResponseSchema);
export const SendPaymentConfirmationApiResponseSchema =
	ApiSuccessResponseSchema(SendEmailResponseSchema);
export const SendTestResultsApiResponseSchema = ApiSuccessResponseSchema(SendEmailResponseSchema);
export const SendWorkflowErrorNotificationApiResponseSchema =
	ApiSuccessResponseSchema(SendEmailResponseSchema);

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Email API Contract
 *
 * Defines the complete API surface for the Email module.
 * Both frontend and backend use this as the single source of truth.
 */
export const EMAIL_API_CONTRACT = createContract('/email', {
	/**
	 * POST /email/send
	 * Send a custom email
	 */
	send: {
		method: 'POST' as const,
		path: '/send',
		body: SendEmailRequestSchema,
		response: SendEmailResponseSchema,
	},

	/**
	 * POST /email/send-template
	 * Send an email using a template
	 */
	sendTemplate: {
		method: 'POST' as const,
		path: '/send-template',
		body: SendTemplateEmailRequestSchema,
		response: SendEmailResponseSchema,
	},

	/**
	 * POST /email/payment-confirmation
	 * Send payment confirmation email
	 */
	sendPaymentConfirmation: {
		method: 'POST' as const,
		path: '/payment-confirmation',
		body: SendPaymentConfirmationRequestSchema,
		response: SendEmailResponseSchema,
	},

	/**
	 * POST /email/test-results
	 * Send test results email with PDF attachment
	 */
	sendTestResults: {
		method: 'POST' as const,
		path: '/test-results',
		body: SendTestResultsRequestSchema,
		response: SendEmailResponseSchema,
	},

	/**
	 * GET /email/logs
	 * Get email logs (admin only)
	 */
	getLogs: {
		method: 'GET' as const,
		path: '/logs',
		query: z
			.object({
				status: EmailStatusSchema.optional(),
				limit: z.coerce.number().int().positive().max(100).default(20),
			})
			.optional(),
		response: z.object({
			logs: z.array(EmailLogSchema),
		}),
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post decorators
 */
export const EMAIL_ROUTES = {
	base: EMAIL_API_CONTRACT.base,
	send: EMAIL_API_CONTRACT.routes.send.path,
	sendTemplate: EMAIL_API_CONTRACT.routes.sendTemplate.path,
	sendPaymentConfirmation: EMAIL_API_CONTRACT.routes.sendPaymentConfirmation.path,
	sendTestResults: EMAIL_API_CONTRACT.routes.sendTestResults.path,
	getLogs: EMAIL_API_CONTRACT.routes.getLogs.path,
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type EmailApiContract = typeof EMAIL_API_CONTRACT;
